import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ErrorBanner, ListState, useAsync } from '../../components/Async'
import { WAYS_IN_COPY } from '../../lib/emptyState'
import { waysInState } from './waysInState'
import { useSavedViews } from '../../components/SavedViews'
import { waysIn, type WayIn, type WayInPage } from '../../lib/api'
import { paramFrom, withParams } from '../../lib/tabs'
import { nextOffset, pageState, previousOffset } from '../explore/paging'

/** One page. Twenty-five rows is what fits a desk screen without the footer leaving the viewport. */
const PAGE = 25

const WAYS_IN = { one: 'kit or token', many: 'kits and tokens' }

/**
 * The filter the tab opens on.
 *
 * Live, not everything. The question somebody arrives with is "what can get into my organization right
 * now", and a development stack answers that with three rows out of fourteen hundred. Opening on All
 * buries those three under every token ever issued, which is the list this screen replaced.
 */
const DEFAULT_STATE = 'live'

/**
 * A date short enough to sit in a 78px column.
 *
 * `formatUtc` renders "9/14/2027, 12:51:45 PM", which wraps a 34px row to three lines - the row height
 * is the design's, and a full timestamp is not what the column is for. The full value is in the
 * inspector, where there is room for it.
 */
function shortDate(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** The five segments, in the order somebody reads them: what works, then why the rest does not. */
const SEGMENTS: { key: string; label: string; count: string }[] = [
  { key: 'live', label: 'Live', count: 'live' },
  { key: 'used-up', label: 'Used up', count: 'used-up' },
  { key: 'expired', label: 'Expired', count: 'expired' },
  { key: 'revoked', label: 'Revoked', count: 'revoked' },
  { key: 'all', label: 'All', count: 'all' }
]

/**
 * Which tint a state takes. The word carries the meaning; the tint only makes it findable.
 *
 * `live` takes the acknowledged pair because it is the working state, and `revoked` the retired pair
 * because somebody switched it off. `used up` and `expired` share the done pair: both are finished,
 * and distinguishing them by color would imply one needs attention.
 */
function chipFor(state: WayIn['state']): string {
  if (state === 'live') return 'chip-ack'
  if (state === 'revoked') return 'chip-retired'
  return 'chip-done'
}

/** The state as an operator says it. Only `used-up` needs unhyphenating. */
function stateLabel(state: WayIn['state']): string {
  return state === 'used-up' ? 'used up' : state
}

/**
 * Every way into this organization, as one list.
 *
 * It replaces two: a kit table and a token table, each capped at ten rows behind its own disclosure,
 * with a sentence above the second counting an array the server had already truncated at a hundred.
 * Building a kit issues exactly one token, so those were one list rendered twice and joined by
 * nothing the reader could see but an eight-character prefix (D-437).
 *
 * The filter, the search, the sort and the page all live in the URL beside `tab`, for the reasons
 * `lib/tabs.ts` gives for the tab itself: the end-to-end suite navigates by URL, and an operator who
 * has narrowed a list of credentials to the one they are about to revoke should not lose it to a
 * refresh (D-441).
 *
 * Rows are `<button>` elements in a grid rather than a `<table>`. That is what lets the six-column set
 * become three when the inspector docks, by swapping one custom property - see `--ways-in-cols` in the
 * stylesheet - and it is the same idiom `.rule-row` already uses.
 */
export function WaysInList({
  selected,
  onSelect,
  onLoaded,
  reloadKey
}: {
  /** The row the inspector is showing, or null. Held by the host so the inspector can share it. */
  selected: string | null
  onSelect: (row: WayIn) => void
  /** Hands the loaded page up, so the host can resolve the selected id without a second request. */
  onLoaded: (page: WayInPage | null) => void
  /** Bumped by the host after a revoke, so the list re-reads what the fleet now says. */
  reloadKey: number
}) {
  const location = useLocation()
  const navigate = useNavigate()

  const state = paramFrom(location.search, 'state') ?? DEFAULT_STATE
  const search = paramFrom(location.search, 'q') ?? ''
  const sort = paramFrom(location.search, 'sort') ?? 'newest'
  const offset = Number(paramFrom(location.search, 'from') ?? 0) || 0

  const page = useAsync(
    () =>
      waysIn.list({
        state: state === 'all' ? undefined : state,
        search: search || undefined,
        sort,
        offset,
        limit: PAGE
      }),
    [state, search, sort, offset, reloadKey]
  )

  // Reported up rather than fetched twice. The host needs the selected row's fields for the pane, and
  // the page it is already showing holds them.
  const loaded = page.value
  useEffect(() => onLoaded(loaded ?? null), [loaded, onLoaded])

  /**
   * Writes the query, and returns to the first page whenever the question changes.
   *
   * Page four of the old result is not page four of the new one, and an operator who narrows a list
   * and lands on an empty page reads it as "nothing matches" (D-441).
   */
  function narrow(changes: Record<string, string | null>) {
    navigate(`/devices${withParams(location.search, { ...changes, from: null })}`, { replace: true })
  }

  function goTo(nextFrom: number) {
    navigate(`/devices${withParams(location.search, { from: nextFrom > 0 ? String(nextFrom) : null })}`, {
      replace: true
    })
  }

  const rows = page.value?.items ?? []
  const counts = page.value?.counts ?? {}

  /*
    The same five segments, in the pane down the left.

    Two controls for one piece of state, deliberately: the pane is the one visible at a glance from
    anywhere on the screen, and the segmented row is where somebody's hand already is. Both write the
    same query parameter, so they cannot disagree.

    The counts are the server's, which is what makes listing them here allowed at all - `ViewsPane`
    held them back until an API could answer them rather than print a plausible number beside a filter
    (D-450). They are left undefined until the page arrives, so the pane prints a label and no figure
    rather than a zero.
  */
  const views = useMemo(
    () =>
      SEGMENTS.map((segment) => ({
        label: segment.label,
        to: `/devices${withParams(location.search, { state: segment.key, from: null })}`,
        current: state === segment.key,
        count: page.value ? (counts[segment.count] ?? 0) : undefined
      })),
    [location.search, state, page.value, counts]
  )

  useSavedViews(views)
  const paging = page.value ? pageState(page.value.total, page.value.offset, rows.length, WAYS_IN) : null

  return (
    <div className="ways-in-list">
      <ErrorBanner message={page.error} />

      <div className="filter-row">
        {/*
          The counts come from the response, not from `rows`. Selecting a segment narrows the rows and
          the total and leaves every count where it was, which is what makes the set readable.
        */}
        <div className="segmented" role="group" aria-label="Filter by state">
          {SEGMENTS.map((segment) => (
            <button
              key={segment.key}
              type="button"
              aria-pressed={state === segment.key}
              onClick={() => narrow({ state: segment.key })}
              data-testid={`ways-in-filter-${segment.key}`}
            >
              <span>{segment.label}</span>
              <span className="segment-count tabular">{counts[segment.count] ?? '—'}</span>
            </button>
          ))}
        </div>

        <input
          type="search"
          className="ways-in-search"
          placeholder="Search"
          aria-label="Search by name or first characters"
          defaultValue={search}
          onChange={(event) => narrow({ q: event.target.value || null })}
          data-testid="ways-in-search"
        />

        <select
          aria-label="Sort"
          className="ways-in-sort"
          value={sort}
          onChange={(event) => narrow({ sort: event.target.value })}
          data-testid="ways-in-sort"
        >
          <option value="newest">Newest</option>
          <option value="expiring">Expiring soonest</option>
        </select>
      </div>

      <div className="ways-in-head" role="presentation">
        <span>Name</span>
        <span className="wide-only">Kind</span>
        <span className="wide-only">First 8</span>
        <span className="right">Machines</span>
        <span className="wide-only right">Expires</span>
        <span className="right">State</span>
      </div>

      <div className="ways-in-rows" data-testid="ways-in-rows">
        {rows.map((row) => {
          const isKit = Boolean(row.deploymentPackageId)

          return (
            <button
              key={row.enrollmentTokenId}
              type="button"
              className={selected === row.enrollmentTokenId ? 'ways-in-row selected' : 'ways-in-row'}
              onClick={() => onSelect(row)}
              data-testid="ways-in-row"
              data-state={row.state}
            >
              <span className="ways-in-bar" aria-hidden="true" />

              <span className="ways-in-name">
                <span className={row.state === 'live' ? 'ways-in-title live' : 'ways-in-title'}>{row.name}</span>
                <span className="ways-in-meta">
                  {isKit ? 'kit' : 'token'} · built {shortDate(row.createdUtc)}
                </span>
              </span>

              <span className="wide-only muted">{isKit ? 'kit' : 'token'}</span>
              <span className="wide-only mono muted">{row.tokenPrefix}…</span>
              <span className="right tabular">{row.uses.toLocaleString()}</span>
              {/* A date only while it can still arrive. A revoked or spent row has no expiry left to
                  run out, and printing one invites somebody to wait for it. */}
              <span className="wide-only right tabular muted">
                {row.state === 'live' ? shortDate(row.expiresUtc) : '—'}
              </span>
              <span className="right">
                <span className={`chip ${chipFor(row.state)}`}>{stateLabel(row.state)}</span>
              </span>
            </button>
          )
        })}
      </div>

      <ListState
        kind={waysInState({ page: page.value, error: page.error, loading: page.loading, state, search })}
        copy={WAYS_IN_COPY}
        columns={6}
        onAction={() => {
          if (page.error) return page.reload()
          narrow({ state: 'all', q: null })
        }}
      />

      {paging && rows.length > 0 && (
        <div className="ways-in-foot">
          <span className="hint grow" data-testid="ways-in-count">
            {paging.summary}
          </span>
          <button
            type="button"
            onClick={() => goTo(previousOffset(offset, PAGE))}
            disabled={!paging.canGoBack}
            data-testid="ways-in-newer"
          >
            Newer
          </button>
          <button
            type="button"
            onClick={() => goTo(nextOffset(page.value!.total, offset, PAGE))}
            disabled={!paging.canGoForward}
            data-testid="ways-in-older"
          >
            Older
          </button>
        </div>
      )}

    </div>
  )
}
